import axios from 'axios'
import { getAccessToken } from './auth'
import { getDatabricksBaseUrl } from './databricksHost'

// ---------------------------------------------------------------------------
// Axios instance for the Databricks REST API. In development, requests go
// through the Vite dev proxy at /api/databricks; in production they go
// directly to the configured Databricks workspace (see databricksHost.ts).
// Authenticated with the MSAL-acquired user access token (see auth.ts).
// ---------------------------------------------------------------------------
const client = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})

client.interceptors.request.use(async (config) => {
  config.baseURL = `${getDatabricksBaseUrl()}/`
  const token = await getAccessToken()
  config.headers['Authorization'] = `Bearer ${token}`
  return config
})

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 400) {
      const body = await error.response?.data
      console.error('Databricks 400 error:', body)
    }
    return Promise.reject(error)
  }
)

// ---------------------------------------------------------------------------
// Types — subset of the Databricks Jobs 2.1 REST API
// https://docs.databricks.com/api/workspace/jobs/listruns
// ---------------------------------------------------------------------------

export type RunLifeCycleState =
  | 'PENDING'
  | 'RUNNING'
  | 'TERMINATING'
  | 'TERMINATED'
  | 'SKIPPED'
  | 'INTERNAL_ERROR'
  | 'BLOCKED'
  | 'WAITING_FOR_RETRY'

export type RunResultState =
  | 'SUCCESS'
  | 'FAILED'
  | 'TIMEDOUT'
  | 'CANCELED'
  | 'MAXIMUM_CONCURRENT_RUNS_REACHED'
  | 'EXCLUDED'
  | 'SUCCESS_WITH_FAILURES'
  | 'UPSTREAM_FAILED'
  | 'UPSTREAM_CANCELED'

export interface RunState {
  life_cycle_state: RunLifeCycleState
  result_state?: RunResultState
  state_message?: string
}

export interface JobRun {
  run_id: number
  job_id: number
  run_name: string
  /** epoch milliseconds */
  start_time: number
  /** epoch milliseconds — only set when run has ended */
  end_time?: number
  /** wall-clock duration in milliseconds */
  execution_duration?: number
  /** includes setup/cleanup time */
  run_duration?: number
  state: RunState
  run_page_url: string
  trigger?: string
  creator_user_name?: string
}

export interface ListRunsResponse {
  runs?: JobRun[]
  has_more?: boolean
  next_page_token?: string
}

export interface ListJobsResponse {
  jobs?: { job_id: number; settings: { name: string } }[]
  has_more?: boolean
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** Fetch the most recent N runs across all jobs. */
export async function listRuns(limit = 25): Promise<JobRun[]> {
  const { data } = await client.get<ListRunsResponse>(
    'api/2.1/jobs/runs/list',
    {
      params: {
        limit,
        expand_tasks: 'false',
        active_only: 'false',
      },
    }
  )
  return data.runs ?? []
}

/** Fetch a single run by ID. */
export async function getRun(runId: number): Promise<JobRun> {
  const { data } = await client.get<JobRun>('api/2.1/jobs/runs/get', {
    params: { run_id: runId },
  })
  return data
}

export type JobSummary = { job_id: number; settings: { name: string } }

/** Fetch jobs list (for name resolution). */
export async function listJobs(limit = 100): Promise<JobSummary[]> {
  const { data } = await client.get<ListJobsResponse>('api/2.1/jobs/list', {
    params: { limit },
  })
  return data.jobs ?? []
}

// ---------------------------------------------------------------------------
// Types — subset of the Databricks Unity Catalog REST API
// https://docs.databricks.com/api/workspace/catalogs
// https://docs.databricks.com/api/workspace/schemas
// https://docs.databricks.com/api/workspace/tables
// ---------------------------------------------------------------------------

export interface UnityCatalogCatalog {
  name: string
  comment?: string
  owner?: string
  catalog_type?: string
  created_at?: number
  updated_at?: number
}

export interface UnityCatalogSchema {
  name: string
  full_name: string
  catalog_name: string
  comment?: string
  owner?: string
  created_at?: number
  updated_at?: number
}

export interface UnityCatalogColumn {
  name: string
  type_text?: string
  type_name?: string
  comment?: string
  position?: number
  nullable?: boolean
}

export interface UnityCatalogTable {
  name: string
  full_name: string
  catalog_name: string
  schema_name: string
  table_type?: string
  data_source_format?: string
  comment?: string
  owner?: string
  created_at?: number
  updated_at?: number
  columns?: UnityCatalogColumn[]
}

interface ListCatalogsResponse {
  catalogs?: UnityCatalogCatalog[]
  next_page_token?: string
}

interface ListSchemasResponse {
  schemas?: UnityCatalogSchema[]
  next_page_token?: string
}

interface ListTablesResponse {
  tables?: UnityCatalogTable[]
  next_page_token?: string
}

// ---------------------------------------------------------------------------
// Unity Catalog API calls
// ---------------------------------------------------------------------------

/** Fetch every catalog in the metastore attached to this workspace. */
export async function listCatalogs(): Promise<UnityCatalogCatalog[]> {
  const catalogs: UnityCatalogCatalog[] = []
  let pageToken: string | undefined

  do {
    const { data } = await client.get<ListCatalogsResponse>(
      'api/2.1/unity-catalog/catalogs',
      { params: { max_results: 100, page_token: pageToken } }
    )
    catalogs.push(...(data.catalogs ?? []))
    pageToken = data.next_page_token
  } while (pageToken)

  return catalogs
}

/** Fetch every schema in a catalog. */
export async function listSchemas(
  catalogName: string
): Promise<UnityCatalogSchema[]> {
  const schemas: UnityCatalogSchema[] = []
  let pageToken: string | undefined

  do {
    const { data } = await client.get<ListSchemasResponse>(
      'api/2.1/unity-catalog/schemas',
      {
        params: {
          catalog_name: catalogName,
          max_results: 100,
          page_token: pageToken,
        },
      }
    )
    schemas.push(...(data.schemas ?? []))
    pageToken = data.next_page_token
  } while (pageToken)

  return schemas
}

/** Fetch a single table by its fully-qualified name. */
export async function getTable(
  tableFullName: string
): Promise<UnityCatalogTable> {
  const { data } = await client.get<UnityCatalogTable>(
    'api/2.1/unity-catalog/tables/' + encodeURIComponent(tableFullName)
  )
  return data
}

const SYSTEM_CATALOGS = new Set(['system', 'samples'])

/** Fetch every table across all non-system catalogs and schemas. */
export async function listAllTables(): Promise<UnityCatalogTable[]> {
  const catalogs = (await listCatalogs()).filter(
    (catalog) => !SYSTEM_CATALOGS.has(catalog.name.toLowerCase())
  )
  const tables: UnityCatalogTable[] = []

  await Promise.all(
    catalogs.map(async (catalog) => {
      const schemas = await listSchemas(catalog.name)
      await Promise.all(
        schemas.map(async (schema) => {
          const schemaTables = await listTables(catalog.name, schema.name)
          tables.push(...schemaTables)
        })
      )
    })
  )

  return tables
}

// ---------------------------------------------------------------------------
// SQL Statement Execution API
// https://docs.databricks.com/api/workspace/statementexecution
// ---------------------------------------------------------------------------

export interface QueryResultColumn {
  name: string
  typeName?: string
}

export interface QueryResult {
  columns: QueryResultColumn[]
  rows: string[][]
  totalRowCount: number
  truncated: boolean
}

interface StatementResponse {
  statement_id: string
  status: { state: string; error?: { message?: string } }
  manifest?: {
    schema?: { columns?: Array<{ name: string; type_name?: string }> }
    total_chunk_count?: number
    total_row_count?: number
    truncated?: boolean
  }
  result?: {
    chunk_index?: number
    row_offset?: number
    row_count?: number
    data_array?: string[][]
  }
}

function statementUrl(statementId: string, path = '') {
  return `api/2.0/sql/statements/${statementId}${path}`
}

async function fetchStatementResult(
  statementId: string
): Promise<StatementResponse & { rows: string[][] }> {
  const { data } = await client.get<StatementResponse>(
    statementUrl(statementId)
  )

  if (data.status.state === 'FAILED') {
    throw new Error(data.status.error?.message || 'Query execution failed.')
  }

  if (data.status.state !== 'SUCCEEDED') {
    throw new Error(
      `Query result not ready yet (status: ${data.status.state}).`
    )
  }

  const rows: string[][] = [...(data.result?.data_array ?? [])]
  const totalChunks = data.manifest?.total_chunk_count ?? 1
  for (let i = 1; i < totalChunks; i++) {
    const { data: chunk } = await client.get<{
      data_array?: string[][]
    }>(statementUrl(statementId, `/result/chunks/${i}`))
    if (chunk.data_array) {
      rows.push(...chunk.data_array)
    }
  }

  return { ...data, rows }
}

export async function executeStatement(
  warehouseId: string,
  statement: string,
  rowLimit = 1000
): Promise<{ statementId: string; result: QueryResult }> {
  const { data } = await client.post<StatementResponse>(
    'api/2.0/sql/statements',
    {
      warehouse_id: warehouseId,
      statement,
      wait_timeout: '0s',
      row_limit: rowLimit,
    }
  )

  const terminalStates = new Set(['SUCCEEDED', 'FAILED', 'CANCELLED', 'CLOSED'])
  let current = data

  while (!terminalStates.has(current.status.state)) {
    await new Promise((resolve) => setTimeout(resolve, 1000))
    const { data: poll } = await client.get<StatementResponse>(
      statementUrl(current.statement_id)
    )
    current = poll
    if (current.status.state === 'FAILED') {
      throw new Error(
        current.status.error?.message || 'Query execution failed.'
      )
    }
    if (
      current.status.state === 'CANCELLED' ||
      current.status.state === 'CLOSED'
    ) {
      throw new Error(`Query was ${current.status.state.toLowerCase()}.`)
    }
  }

  const resultData = await fetchStatementResult(current.statement_id)
  const columns = (resultData.manifest?.schema?.columns ?? []).map((c) => ({
    name: c.name,
    typeName: c.type_name,
  }))

  return {
    statementId: current.statement_id,
    result: {
      columns,
      rows: resultData.rows,
      totalRowCount:
        resultData.manifest?.total_row_count ?? resultData.rows.length,
      truncated: Boolean(resultData.manifest?.truncated),
    },
  }
}

export function getQueryEditorUrl(queryId?: string): string | null {
  const host = import.meta.env.VITE_DATABRICKS_HOST as string | undefined
  if (!host) return null
  const base = host.replace(/\/$/, '')
  const match = base.match(/adb-(\d+)\.\d+\.azuredatabricks\.net$/)
  const orgId = match?.[1]
  const queryPath = queryId ? `/editor/queries/${queryId}` : '/sql/queries'
  const params = new URLSearchParams({ contextId: 'workspace' })
  if (orgId) params.set('o', orgId)
  return `${base}${queryPath}?${params.toString()}`
}

// ---------------------------------------------------------------------------
// Databricks SQL queries API
// https://docs.databricks.com/api/workspace/queries
// ---------------------------------------------------------------------------

export interface DatabricksQuery {
  id: string
  /** Display name of the saved query. */
  name: string
  query: string
  description?: string
  warehouse_id?: string
}

interface ListQueriesItem {
  id?: string
  display_name?: string
  query_text?: string
  description?: string
  warehouse_id?: string
}

interface ListQueriesResponse {
  results?: ListQueriesItem[]
}

function toDatabricksQuery(item: ListQueriesItem): DatabricksQuery {
  return {
    id: item.id ?? '',
    name: item.display_name ?? 'Untitled query',
    query: item.query_text ?? '',
    description: item.description,
    warehouse_id: item.warehouse_id,
  }
}

export async function listQueries(): Promise<DatabricksQuery[]> {
  const { data } = await client.get<ListQueriesResponse>(
    'api/2.0/sql/queries',
    {
      params: { page_size: 100 },
    }
  )
  return (data.results ?? []).map(toDatabricksQuery)
}

export async function createQuery(
  query: Omit<DatabricksQuery, 'id'>
): Promise<DatabricksQuery> {
  const { data } = await client.post<ListQueriesItem>('api/2.0/sql/queries', {
    query: {
      display_name: query.name,
      query_text: query.query,
      description: query.description ?? '',
      warehouse_id: query.warehouse_id,
    },
  })
  return toDatabricksQuery(data)
}

export async function updateQuery(
  query: DatabricksQuery
): Promise<DatabricksQuery> {
  const { data } = await client.post<ListQueriesItem>(
    `api/2.0/sql/queries/${query.id}`,
    {
      update_mask: 'display_name,query_text,description,warehouse_id',
      query: {
        display_name: query.name,
        query_text: query.query,
        description: query.description ?? '',
        warehouse_id: query.warehouse_id,
      },
    }
  )
  return toDatabricksQuery(data)
}

export interface SqlWarehouse {
  id: string
  name: string
  workspace_id?: number
}

export async function listWarehouses(): Promise<SqlWarehouse[]> {
  const { data } = await client.get<{ warehouses?: SqlWarehouse[] }>(
    'api/2.0/sql/warehouses',
    { params: { page_size: 100 } }
  )
  return data.warehouses ?? []
}

/** Fetch every table (including its columns) in a schema. */
export async function listTables(
  catalogName: string,
  schemaName: string
): Promise<UnityCatalogTable[]> {
  const tables: UnityCatalogTable[] = []
  let pageToken: string | undefined

  do {
    const { data } = await client.get<ListTablesResponse>(
      'api/2.1/unity-catalog/tables',
      {
        params: {
          catalog_name: catalogName,
          schema_name: schemaName,
          max_results: 100,
          page_token: pageToken,
        },
      }
    )
    tables.push(...(data.tables ?? []))
    pageToken = data.next_page_token
  } while (pageToken)

  return tables
}
